import { useMemo, useState } from "react";
import { ClassNames } from "@emotion/react";
import styled from "@emotion/styled";
import { useNavigate } from "@tanstack/react-router";
import { Input, Select } from "antd5";
import { isEqual } from "lodash";
import { Flex, Text } from "styles/utility-components";

import { FormattedDate } from "components/app_layout/Typography";
import { ProviderType } from "components/integrations/managed_integrations/types";
import { AssignedToCell } from "components/table_components/AssignedToCell";
import Button from "components/ui/button/Button";
import { DropdownMenu, DropdownMenuItem } from "components/ui/dropdown_menu";
import EmptyState from "components/ui/empty_state/EmptyState";
import toast from "components/ui/toast/Toast";
import { EllipsisTooltipText } from "lib/core_components/EllipsisTooltip";
import { ColumnType, commonTableColumns } from "lib/core_components/Table/ColumnTypes";
import { Table } from "lib/core_components/Table/Table";
import { useDebouncedValue } from "lib/debounce";
import { CONTENT_LIBRARY_MANAGEMENT, useVariableValue } from "lib/featureFlags";
import { useCompanyDocumentLabels } from "lib/hooks/api/customer_documents/useCompanyDocumentLabels";
import {
  CustomerDocumentDetails,
  CustomerDocumentSortState,
  useCustomerDocuments,
} from "lib/hooks/api/customer_documents/useCustomerDocuments";
import { useDownloadCustomerDocument } from "lib/hooks/api/customer_documents/useDownloadCustomerDocument";
import { useMarkDocumentAsReviewed } from "lib/hooks/api/customer_documents/useMarkDocumentAsReviewed";
import { useLogSearch } from "lib/hooks/api/userActivity/useLogSearch";
import { useTablePageSize } from "lib/hooks/useTablePageSize";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { SortState } from "lib/search/types";
import {
  CustomerDocumentReviewStatus,
  CustomerDocumentSortBy,
  CustomerDocumentSortOrder,
  CustomerDocumentTypeFilter,
  DeleteDocumentType,
  LoggedEntityType,
} from "lib/types/graphQLEnums";
import { deduplicateByKey } from "lib/utils";
import { CustomerDocumentDrawer } from "../customer_documents/CustomerDocumentDrawer";
import {
  AssigneeFilterValue,
  UserDropdownFilter,
} from "../form_components/dropdown_list_selects/UserDropdownFilter";
import { DeleteCustomerDocumentModal } from "./modals/DeleteCustomerDocumentModal";
import { MoveCustomerDocumentModal } from "./modals/MoveCustomerDocumentModal";
import { RenameCustomerDocumentModal } from "./modals/RenameCustomerDocumentModal";
import ContentLibraryActionBar from "./ContentLibraryActionBar";
import ContentLibraryActionCards from "./ContentLibraryActionCards";
import DocumentLabels from "./DocumentLabels";
import ReviewStatusTag, { getReviewStatus } from "./ReviewStatusTag";
import UploadContentLibrary from "./UploadContentLibrary";
import { buildContentLibraryPath } from "./utils";

function isFilename(path: string): boolean {
  return /[^\\/]+\.[a-zA-Z0-9]+$/.test(path);
}

function getSortOrder(sort: SortState, key: string): "ascend" | "descend" | null {
  return sort.field !== key ? null : sort.order === "ASC" ? "ascend" : "descend";
}

type Props = {
  provider?: ProviderType | "uploads";
  folderPath?: string;
  reviewStatus?: CustomerDocumentReviewStatus[];
  hideActionAndDescription?: boolean;
  initialOwnerFilter?: AssigneeFilterValue;
};
export default function ContentLibraryTable({
  provider = "uploads",
  folderPath,
  reviewStatus,
  initialOwnerFilter,
  hideActionAndDescription = false,
}: Props) {
  const isLocalContentLibraryEntry = provider === "uploads";
  const isContentLibraryManagementEnabled = useVariableValue(CONTENT_LIBRARY_MANAGEMENT, false);

  const { mutate: markAsReviewed } = useMarkDocumentAsReviewed({
    onSuccess: () => {
      toast.success("Document marked as reviewed");
    },
    onError: () => {
      toast.error("Failed to mark document as reviewed");
    },
  });

  const [selectedDocuments, setSelectedDocuments] = useState<CustomerDocumentDetails[]>([]);
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useTablePageSize("content-library", 10);
  const [textSearch, setTextSearch] = useState("");
  const [debouncedTextSearch, setDebouncedTextValue] = useDebouncedValue(textSearch, 400);
  const [displayDocument, setDisplayDocument] = useState<CustomerDocumentDetails | null>(null);

  const [sort, setSort] = useState<CustomerDocumentSortState>({
    field: CustomerDocumentSortBy.CreatedAt,
    order: CustomerDocumentSortOrder.Desc,
  });

  const [ownerFilter, setOwnerFilter] = useState<AssigneeFilterValue>(
    initialOwnerFilter ?? { ids: [], includeNulls: false },
  );
  const [labelFilter, setLabelFilter] = useState<string[]>([]);

  const activeOwnerFilter =
    ownerFilter.ids.length > 0 || ownerFilter.includeNulls
      ? { ids: ownerFilter.ids, includeNulls: ownerFilter.includeNulls }
      : undefined;
  useLogSearch(LoggedEntityType.CustomerDocument, {
    debouncedTextSearch,
    ownerFilter,
    labelFilter,
    folderPath,
  });

  const {
    data: documents,
    totalCount,
    isError,
    isLoading,
  } = useCustomerDocuments(
    {
      documentType: CustomerDocumentTypeFilter.ContentLibrary,
      originSource: isLocalContentLibraryEntry ? undefined : provider,
      // With uiPath being null, when there is no folderPath it will ONLY
      // fetch root documents - if it's left as undefined it will fetch all
      // documents
      uiPath: folderPath,
      // When filtering by review status, owner, or labels, fetch across all folders
      onlyFetchFiles:
        (reviewStatus && reviewStatus.length > 0) || activeOwnerFilter || labelFilter.length > 0
          ? true
          : undefined,
      sortBy: sort.field,
      sortOrder: sort.order,
      textSearch: debouncedTextSearch,
      reviewStatus: reviewStatus,
      owner: activeOwnerFilter,
      labels: labelFilter.length > 0 ? labelFilter : undefined,
    },
    {
      page: currentPage,
      pageSize,
    },
  );

  const tableColumns: ColumnType<CustomerDocumentDetails>[] = useMemo(
    () => [
      {
        ...commonTableColumns.titleColumn,
        title: "Name",
        key: "fileName",
        sorter: true,
        sortOrder: getSortOrder(sort, "fileName"),
        sortDirections: ["descend", "ascend", "descend"],

        // No locations array because we're just opening the general doc here.
        // There's no pointing to specific sources involved
        onCell: (doc) => ({
          onClick: () => {
            if (doc.isFolder) {
              const folderPath = doc.uiPath ? `${doc.uiPath}/${doc.fileName}` : doc.fileName;
              setTextSearch("");
              setDebouncedTextValue("");
              void navigate({ to: buildContentLibraryPath(provider, folderPath) });
            } else {
              setDisplayDocument(doc);
            }
          },
          style: { cursor: "pointer" },
        }),
        render: ({ fileName }: CustomerDocumentDetails) => (
          <Flex alignItems="center" gap={8}>
            <UIcon
              icon={isFilename(fileName) ? "file06" : "folder"}
              color="sysTextDefault"
              size={16}
            />
            <ClassNames>
              {({ css }) => (
                <EllipsisTooltipText
                  fullText={fileName}
                  containerClassname={css({
                    display: "-webkit-box",
                    WebkitLineClamp: 1,
                    lineClamp: 1,
                    WebkitBoxOrient: "vertical",
                    fontWeight: 600,
                  })}
                />
              )}
            </ClassNames>
          </Flex>
        ),
      },
      ...(isContentLibraryManagementEnabled
        ? [
            {
              key: "reviewStatus",
              title: "",
              render: (doc: CustomerDocumentDetails) => (
                <ReviewStatusCell doc={doc} markAsReviewed={markAsReviewed} />
              ),
            },
            {
              key: "labels",
              title: "",
              render: (doc: CustomerDocumentDetails) => (
                <DocumentLabels labels={doc.labels ?? []} isFolder={doc.isFolder} />
              ),
            },
            {
              key: "owner",
              title: "Owner",
              render: ({ assignedTo }: CustomerDocumentDetails) => (
                <AssignedToCell assignedTo={assignedTo} />
              ),
            },
          ]
        : []),
      {
        ...commonTableColumns.dateColumn,
        title: "Date added",
        key: "createdAt",
        sorter: true,
        sortOrder: getSortOrder(sort, "createdAt"),
        sortDirections: ["descend", "ascend", "descend"],
        render: ({ createdAt }: CustomerDocumentDetails) => (
          <Text>{FormattedDate({ date: createdAt })}</Text>
        ),
      },
      {
        sizeConfig: commonTableColumns.actionColumn.sizeConfig,
        key: "actions",
        title: (
          <Flex alignItems="center" justifyContent="center">
            <UIcon icon="dotsVertical" color="sysTextDefault" />
          </Flex>
        ),
        render: (doc: CustomerDocumentDetails) => {
          return (
            <Flex alignItems="center" justifyContent="center">
              <ActionColumn
                hasPermissionsToModify={isLocalContentLibraryEntry}
                document={doc}
                setSelectedDocuments={setSelectedDocuments}
              />
            </Flex>
          );
        },
      },
    ],
    [
      provider,
      isLocalContentLibraryEntry,
      isContentLibraryManagementEnabled,
      markAsReviewed,
      setDebouncedTextValue,
      navigate,
      sort,
    ],
  );

  const noPagination = currentPage === 1 && pageSize >= totalCount;

  return (
    <>
      {selectedDocuments.length > 0 ? (
        <ContentLibraryActionBar
          selectedDocuments={selectedDocuments}
          provider={provider}
          hasPermissionsToModify={isLocalContentLibraryEntry}
          onClearSelection={() => setSelectedDocuments([])}
        />
      ) : (
        <>
          {!hideActionAndDescription && (
            <>
              {/* Only show description if we're not in a folder */}
              {isLocalContentLibraryEntry && !folderPath && (
                <Description>
                  Your content library is a centralised repository of past proposals, case studies,
                  and relevant documents. It helps streamline the response process by providing easy
                  access to historical data and insights.
                </Description>
              )}
              {/* Only show modify action cards if not in a cms route */}
              <ContentLibraryActionCards
                hasDocuments={documents.length > 0}
                hasPermissionsToModify={isLocalContentLibraryEntry}
                provider={provider}
                uiPath={folderPath}
              />
            </>
          )}
        </>
      )}
      {/* Only show this component when there are no documents at all - the start state essentially */}
      {!textSearch &&
      !activeOwnerFilter &&
      !reviewStatus?.length &&
      !labelFilter.length &&
      !documents.length &&
      !isLoading &&
      !isError ? (
        <Flex flexDirection="column" aria-label="Content library empty state" alignItems="center">
          {/* Only show when there's no documents in the root path */}
          {isLocalContentLibraryEntry && !folderPath && (
            <UploadContentLibrary isModal={false} uiPath={folderPath} />
          )}
          <EmptyState
            type="noResults"
            heading="No files yet"
            description="Upload files or folders to populate your content library"
            size="large"
            width="100%"
          />
        </Flex>
      ) : (
        <Flex flexDirection="column" gap={16}>
          <Flex gap={8} alignItems="center">
            <InputContainer>
              <Input
                placeholder={`Search in ${folderPath ?? "content library"}`}
                onChange={(e) => setTextSearch(e.currentTarget.value.trim())}
                value={textSearch}
                prefix={<UIcon icon="searchSm" color="sysPrimaryDefault" size={16} />}
              />
            </InputContainer>
            {isContentLibraryManagementEnabled && (
              <>
                <UserDropdownFilter
                  value={ownerFilter}
                  onChange={setOwnerFilter}
                  options={{ noun: "owner" }}
                />
                <LabelFilterDropdown value={labelFilter} onChange={setLabelFilter} />
              </>
            )}
          </Flex>
          <Table<CustomerDocumentDetails>
            ariaLabel="Customer documents Table"
            isError={isError}
            loading={isLoading}
            rowKey="id"
            columns={tableColumns}
            dataSource={documents}
            onChange={(_, __, sorter) => {
              if (Array.isArray(sorter)) {
                sorter = sorter[0];
              }
              // If the sort order is not defined, we don't want to trigger a sort without an order
              if (!sorter.order) {
                return;
              }
              const newSortState: CustomerDocumentSortState = {
                order:
                  sorter.order === "descend"
                    ? CustomerDocumentSortOrder.Desc
                    : CustomerDocumentSortOrder.Asc,
                field: sorter.columnKey as CustomerDocumentSortBy,
              };

              if (!isEqual(newSortState, sort)) {
                setSort(newSortState);
              }
            }}
            pagination={
              noPagination
                ? undefined
                : {
                    current: currentPage,
                    pageSize,
                    total: totalCount,
                    onChange: (page, pageSize) => {
                      setPageSize(pageSize);
                      setCurrentPage(page);
                    },
                    showSizeChanger: true,
                  }
            }
            rowSelection={{
              onChange: (selectedDocIds) => {
                const newSelectedDocs =
                  documents.filter((doc) => selectedDocIds.includes(doc.id)) ?? [];
                const retainedDocs = selectedDocuments.filter((o) => selectedDocIds.includes(o.id));

                const allDocs = Array.from(
                  deduplicateByKey(
                    [...newSelectedDocs, ...retainedDocs],
                    (org: CustomerDocumentDetails) => {
                      return org.id;
                    },
                  ),
                );
                setSelectedDocuments(allDocs);
              },
              preserveSelectedRowKeys: true,
              selectedRowKeys: selectedDocuments.map((doc) => doc.id),
            }}
          />
        </Flex>
      )}

      <CustomerDocumentDrawer
        source={{
          documentId: displayDocument?.id ?? "",
          fileName: displayDocument?.fileName ?? "",
          locations: [],
        }}
        onClose={() => setDisplayDocument(null)}
        isOpen={!!displayDocument}
      />
    </>
  );
}

type ReviewStatusCellProps = {
  doc: CustomerDocumentDetails;
  markAsReviewed: (variables: { docId: string }) => void;
};

function ReviewStatusCell({ doc, markAsReviewed }: ReviewStatusCellProps) {
  const status = getReviewStatus(doc.reviewDueDate);

  // Only show the tag and dropdown for due and overdue documents
  if (status !== "due" && status !== "overdue") {
    return null;
  }

  return (
    <DropdownMenu
      trigger={
        <ReviewStatusTrigger aria-label="Review status actions">
          <ReviewStatusTag reviewDueDate={doc.reviewDueDate} />
        </ReviewStatusTrigger>
      }
      width="184px"
    >
      <DropdownMenuItem
        key="markAsReviewed"
        icon="checkSquare"
        aria-label="Mark as reviewed"
        onClick={() => markAsReviewed({ docId: doc.id })}
      >
        Mark as reviewed
      </DropdownMenuItem>
    </DropdownMenu>
  );
}

const ReviewStatusTrigger = styled(Flex)({
  all: "unset",
  cursor: "pointer",
  display: "inline-flex",
});

const Description = styled(Text)({
  maxWidth: 650,
});

const InputContainer = styled.div({
  width: 250,
});

type LabelFilterDropdownProps = {
  value: string[];
  onChange: (value: string[]) => void;
};

function LabelFilterDropdown({ value, onChange }: LabelFilterDropdownProps) {
  const { labels } = useCompanyDocumentLabels();

  return (
    <Select
      mode="multiple"
      placeholder="Filter by label"
      value={value}
      onChange={onChange}
      options={labels.map((l: string) => ({ label: l, value: l }))}
      style={{ minWidth: 160 }}
      allowClear
      maxTagCount="responsive"
    />
  );
}

type ActionColumnProps = {
  hasPermissionsToModify: boolean;
  document: CustomerDocumentDetails;
  setSelectedDocuments: React.Dispatch<React.SetStateAction<CustomerDocumentDetails[]>>;
};
function ActionColumn({
  hasPermissionsToModify,
  document,
  setSelectedDocuments,
}: ActionColumnProps) {
  const [isRenameModalOpen, setRenameModalOpen] = useState(false);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isMoveModalOpen, setMoveModalOpen] = useState(false);

  const { mutate: downloadDocument } = useDownloadCustomerDocument({
    onSuccess: async (_data, vars) => {
      toast.success(`Successfully downloaded document ${vars.fileName}`);
    },
    onError: (_error, vars) => {
      toast.error(`Failed to download document ${vars.fileName}`);
    },
  });

  if (!hasPermissionsToModify) return <></>;

  return (
    <>
      <DropdownMenu
        trigger={
          <Button
            icon="dotsVertical"
            buttonType="tertiary"
            iconProps={{ color: "sysTextDefault" }}
          />
        }
        width="160px"
        align="end"
      >
        <DropdownMenuItem
          key="renamFsetQueriesDatye"
          icon="edit05"
          onClick={() => setRenameModalOpen(true)}
          aria-label="Rename"
        >
          Rename
        </DropdownMenuItem>
        <DropdownMenuItem
          key="move"
          icon="move"
          onClick={() => setMoveModalOpen(true)}
          aria-label="move"
        >
          Move
        </DropdownMenuItem>
        <DropdownMenuItem
          key="download"
          aria-label="Download"
          icon="download01"
          onClick={() => downloadDocument({ ...document })}
        >
          Download
        </DropdownMenuItem>
        <DropdownMenuItem
          key="delete"
          aria-label="Delete"
          icon="trash03"
          onClick={() => setDeleteModalOpen(true)}
        >
          Delete
        </DropdownMenuItem>
      </DropdownMenu>

      <RenameCustomerDocumentModal
        docId={document.id}
        fileName={document.fileName}
        isOpen={isRenameModalOpen}
        onOpenChange={setRenameModalOpen}
        isFolder={document.isFolder}
      />
      <DeleteCustomerDocumentModal
        selectedDocuments={[document]}
        isOpen={isDeleteModalOpen}
        onOpenChange={setDeleteModalOpen}
        onSuccess={() => {
          setSelectedDocuments((prev) => prev.filter((doc) => document.id !== doc.id));
        }}
        documentType={DeleteDocumentType.ContentLibrary}
      />
      <MoveCustomerDocumentModal
        selectedDocuments={[document]}
        isOpen={isMoveModalOpen}
        onOpenChange={setMoveModalOpen}
        onSuccess={() => {
          setSelectedDocuments((prev) => prev.filter((doc) => document.id !== doc.id));
        }}
      />
    </>
  );
}
